import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { RootState } from 'store/configureStore';
import {
  ControlMaintenance,
  ControlMaintenanceTableRow
} from 'store/ducks/Reporting/types';
import CustomTable from 'components/Tables/CustomTable';
import ApiService from 'utils/ApiCalls';
import * as Styled from 'components/Layouts/Layout.styled';
import * as LocalStyled from './ControlMaintenance.styled';
import * as Constants from './ControlMaintenance.constants';
import * as ReportingStyled from '../Reporting.styled';
import { useReportExport } from 'utils/hooks/useReportExport';
import type { ExcelColumn } from 'utils/hooks/exportReportToExcel';

const { useToken } = theme;

const ControlMaintenanceReport: React.FC = () => {
  const { token } = useToken();

  const [controlData, setControlData] = useState<ControlMaintenanceTableRow[]>(
    []
  );

  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);

  const [shouldFetch, setShouldFetch] = useState(false);

  const [activeSearch] = useState<string | null>(null);

  const [searchValues] = useState<Record<string, string>>({});

  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  const { registerReport, clearReport } = useReportExport();

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    const fetchControls = async () => {
      setLoading(true);

      try {
        const response = await ApiService.getControlMaintenance(
          selectedDate?.format('YYYY-MM-DD')
        );

        if (
          response.data?.status === 'success' &&
          Array.isArray(response.data.data)
        ) {
          const rows: ControlMaintenanceTableRow[] = response.data.data.flatMap(
            (control: ControlMaintenance) =>
              control.instances.map((instance) => ({
                key: `${control.id}_${instance.instance_id}`,

                // Organization
                group_name: instance.group?.name ?? null,

                division_name: instance.division?.name ?? null,

                region_name: instance.region?.name ?? null,

                legal_entity_name: instance.legal_entity?.name ?? null,

                business_unit_name: instance.business_unit?.name ?? null,

                // Control
                control_name: control.control_name ?? null,

                control_description: control.control_description ?? null,

                key_control: control.key_control ?? null,

                // Control 1
                control_type_name:
                  control.control_1?.control_type?.name ?? null,

                owner_name: instance.control_1?.owner?.name ?? null,

                control_overall_effectiveness_name:
                  instance.control_1?.control_overall_effectiveness?.name ??
                  null,

                // Control 2
                design_effectiveness_name:
                  instance.control_2?.design_effectiveness?.name ?? null,

                // Control 3
                operating_effectiveness_name:
                  instance.control_3?.operating_effectiveness?.name ?? null,

                // Control 4
                control_attestation_effectiveness_name:
                  instance.control_4?.control_attestation_effectiveness?.name ??
                  null,

                control_effectiveness_name:
                  instance.control_4?.control_effectiveness ?? null
              }))
          );

          setControlData(rows);
        } else {
          setControlData([]);
        }
      } catch (error) {
        console.error('Failed to fetch control maintenance:', error);

        setControlData([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchControls();
  }, [shouldFetch, selectedDate]);

  const handleDisplay = () => {
    setShouldFetch(true);
  };

  const filteredControlData = useMemo(() => {
    if (!Object.values(searchValues).some(Boolean)) {
      return controlData;
    }

    return controlData.filter((record) => {
      return Object.entries(searchValues).every(([field, searchValue]) => {
        if (!searchValue) {
          return true;
        }

        const recordValue = record[field as keyof ControlMaintenanceTableRow];

        return String(recordValue ?? '')
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    });
  }, [controlData, searchValues]);

  const columns = useMemo<ColumnsType<ControlMaintenanceTableRow>>(
    () =>
      Constants.processControlMaintenanceColumns(
        controlData
      ) as unknown as ColumnsType<ControlMaintenanceTableRow>,
    [controlData, activeSearch, searchValues]
  );

  useEffect(() => {
    registerReport(
      filteredControlData,
      columns as unknown as ExcelColumn<any>[],
      'Control_Maintenance',
      'Control Maintenance'
    );
  }, [filteredControlData, columns, registerReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  return (
    <>
      <LocalStyled.GlobalDatePickerStyle token={token} />

      <ReportingStyled.ReportTableWrapper>
        <LocalStyled.RisksControlsGapsFilterBar>
          <Styled.HorizontalFilterBar token={token} scheme={scheme}>
            <Styled.FilteredContainerRisks>
              <LocalStyled.StyledDatePicker
                placeholder='Date'
                value={selectedDate}
                onChange={(date) => setSelectedDate(date as dayjs.Dayjs | null)}
                popupClassName='white-bg-datepicker'
                getPopupContainer={(trigger) =>
                  trigger.parentElement || document.body
                }
              />

              <Styled.Spacer />

              <Styled.StyledButton
                type='primary'
                onClick={handleDisplay}
                className='display-save-button'
              >
                Display
              </Styled.StyledButton>
            </Styled.FilteredContainerRisks>
          </Styled.HorizontalFilterBar>
        </LocalStyled.RisksControlsGapsFilterBar>

        <LocalStyled.RisksControlsGapsTableWrapper>
          <CustomTable
            drawTable
            itemsCount={filteredControlData.length}
            columns={columns}
            dataSource={filteredControlData}
            loading={loading}
            rowKey='key'
            scroll={{
              x: 4000,
              y: 350
            }}
            sticky
            scheme={scheme}
          />
        </LocalStyled.RisksControlsGapsTableWrapper>
      </ReportingStyled.ReportTableWrapper>
    </>
  );
};

export default ControlMaintenanceReport;
